import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { Select } from "components/inputs";
import React, { useCallback, useMemo } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import {
  useCreateMSAReReviewMutation,
  useGetMSAByIdQuery,
} from "services/msa-api";
import { extractErrorMessage } from "utils/error-utils";
import { useConstants } from "utils/hooks";
import { useDispatch } from "utils/redux";

import { KaboodleToaster } from "../common";
import KaboodleModal from "./KaboodleModal";

type ModalProps = {
  msaId;
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  category: string;
};

const CreateReReviewModal: React.FC<ModalProps> = ({
  isOpen,
  msaId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { constants } = useConstants();

  const { data: msa } = useGetMSAByIdQuery(
    {
      msaId,
    },
    {
      skip: !isOpen || !msaId, // only fetch when modal is open
    },
  );

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormData>();

  const [createMSAReview] = useCreateMSAReReviewMutation();

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        await createMSAReview({
          msaId: msaId,
          category: data.category["value"],
        }).unwrap();
        KaboodleToaster.success("ReReview created successfully");
        onClose();
      } catch (err) {
        const errMsg = `Failed to create ReReview: ${extractErrorMessage(err)}`;
        KaboodleToaster.error(errMsg);
      }
    },
    [createMSAReview, msaId, reset, onClose, history, dispatch],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Add Re-Review">
        <Controller
          control={control}
          name="category"
          rules={{ required: "Category is required" }}
          render={({ field: { name, onChange, value } }) => (
            <Select
              name={name}
              label="Category"
              errors={errors}
              options={
                constants.MSA_RE_REVIEW_CATEGORY.filter((option) =>
                  msa?.remaining_re_review_categories?.includes(option.value),
                ) || []
              }
              value={constants?.MSA_RE_REVIEW_CATEGORY?.find(
                (option) => option.value === value,
              )}
              onChange={onChange}
              required={true}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateReReviewModal;
